"use client"
import { handleErrors, post } from "@/src/api/apiHandler";
import useLoginStore from "@/src/store/useLoginStore";
import useUserStore from "@/src/store/useUser";
import { PersianNum } from "@/src/tools";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import React, { useMemo, useState } from "react";

interface FavoriteType {
    favorite_name: "like" | "dislike";
    favorite_status: number;
    UserId: number;
}

interface FavoriteCommentProps {
    id: number;
    ProductId: number;
    ProductIdentityId: number;
    favorites: FavoriteType[];
    onSuccess: () => void;
}

const FavoriteComment: React.FC<FavoriteCommentProps> = ({
    id,
    ProductId,
    ProductIdentityId,
    favorites,
    onSuccess,
}) => {
    const [loading, setLoading] = useState(false);
    const { dataUser } = useUserStore();
    const { openLogin } = useLoginStore();

    const token = dataUser?.token;
    const userId = dataUser?.id;

    // Calculate counts
    const likeCount = useMemo(
        () => favorites?.filter(f => f.favorite_name === 'like' && f.favorite_status === 1).length,
        [favorites]
    );
    const dislikeCount = useMemo(
        () => favorites?.filter(f => f.favorite_name === 'dislike' && f.favorite_status === 1).length,
        [favorites]
    );

    // Determine current user's action
    const userAction = useMemo(() => {
        const fav = favorites?.find(f => f.UserId === userId && f.favorite_status === 1);
        return fav ? fav.favorite_name : null;
    }, [favorites, userId]);

    const handleClick = async (action: 'like' | 'dislike') => {
        if (!token) {
            openLogin();
            return;
        }
        if (loading) return;
        setLoading(true);
        try {
            await post('/favorite-like', {
                favorite_type: 'comment',
                favorite_name: action,
                CommentId: id,
                ProductId,
                ProductIdentityId,
                ArticleId: null,
            });
            onSuccess();
        } catch (err) {
            handleErrors(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center gap-4">
            <button
                onClick={() => handleClick('like')}
                disabled={loading}
                aria-pressed={userAction === 'like'}
                aria-label={`لایک (${likeCount})`}
                className={`flex items-center gap-1 ${userAction === 'like' ? 'text-blue-600' : 'text-gray-600'} disabled:opacity-50`}
            >
                <ThumbsUp size={20} />
                <span>{PersianNum(likeCount)}</span>
            </button>

            <button
                onClick={() => handleClick('dislike')}
                disabled={loading}
                aria-pressed={userAction === 'dislike'}
                aria-label={`دیسلایک (${dislikeCount})`}
                className={`flex items-center gap-1 ${userAction === 'dislike' ? 'text-red-600' : 'text-gray-600'} disabled:opacity-50`}
            >
                <ThumbsDown size={20} />
                <span>{PersianNum(dislikeCount)}</span>
            </button>
        </div>
    );
};

export default FavoriteComment;